import { useCallback, useEffect, useMemo, useState } from "react";
import "./TaskStudioPage.css";
import { apiDelete, apiGet, apiPost, apiPut, createJobPoller, forgetRememberedJob, readRememberedJob, rememberJob } from "../apiClient";
import {
  ACTIVE_AGENT_STATUSES,
  ACTIVE_JOB_STATUSES,
  AGENT_STATUS_WAITING_FOR_CLARIFICATION,
  DEFAULT_FIBONACCI_COMPLEXITY,
  DELIVERY_STATUS_BACKLOG,
  DELIVERY_STATUSES,
  FIBONACCI_COMPLEXITIES,
  JIRA_STATUS_DRAFT,
  PROFILE_TYPE_AI_AGENT,
  PROFILE_TYPE_HUMAN,
  TICKET_TYPE_STORY,
  TICKET_TYPE_SUBTASK,
  VIEW_SCOPE_WORKSPACE
} from "../domainCatalog";
import { AgentJobPanel, ListField, TicketRow } from "./taskStudio/TaskStudioComponents";
import {
  agentModelLabel,
  currentPublishEvent,
  formatDate,
  jobStatusMessage,
  moveBacklogTicket,
  publishedTicketNames,
  refreshSelected,
  toDraft,
  updateDraft,
  withoutAssignment
} from "./taskStudio/taskStudioUtils";

export { moveBacklogTicket } from "./taskStudio/taskStudioUtils";


const FIBONACCI = FIBONACCI_COMPLEXITIES;
const BACKLOG_GENERATION_JOB_KEY = "taskit.backlogGenerationJob";

const emptyStory = {
  ticket_type: TICKET_TYPE_STORY,
  parent_id: null,
  assignee_id: null,
  reviewer_id: null,
  title: "New story",
  description: "",
  acceptance_criteria: [],
  dependencies: [],
  assumptions: [],
  complexity: DEFAULT_FIBONACCI_COMPLEXITY,
  status: JIRA_STATUS_DRAFT,
  delivery_status: DELIVERY_STATUS_BACKLOG
};

const TaskStudioPage = ({ token, model, scope = "workstream", onUsageChange }) => {
  const [tickets, setTickets] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [savedDraft, setSavedDraft] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [instruction, setInstruction] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [publishingCurrent, setPublishingCurrent] = useState("");
  const [publishedNames, setPublishedNames] = useState([]);
  const [publishJob, setPublishJob] = useState(null);
  const [backlogJob, setBacklogJob] = useState(null);
  const [draggedTicketId, setDraggedTicketId] = useState(null);
  const [agentJob, setAgentJob] = useState(null);
  const [clarificationAnswer, setClarificationAnswer] = useState("");

  const selected = tickets.find((ticket) => ticket.id === selectedId);
  const stories = useMemo(() => tickets.filter((ticket) => ticket.ticket_type === TICKET_TYPE_STORY), [tickets]);
  const membersById = useMemo(
    () => Object.fromEntries(teamMembers.map((member) => [member.id, member])),
    [teamMembers]
  );
  const humanMembers = useMemo(
    () => teamMembers.filter((member) => (member.profile_type || PROFILE_TYPE_HUMAN) === PROFILE_TYPE_HUMAN),
    [teamMembers]
  );
  const aiMembers = useMemo(
    () => teamMembers.filter((member) => member.profile_type === PROFILE_TYPE_AI_AGENT),
    [teamMembers]
  );
  const selectedExecutor = draft ? membersById[draft.assignee_id] : null;
  const requiresReviewer = selectedExecutor?.profile_type === PROFILE_TYPE_AI_AGENT;
  const hasUnsavedChanges = draft && savedDraft && JSON.stringify(draft) !== JSON.stringify(savedDraft);
  const workspaceView = scope === VIEW_SCOPE_WORKSPACE;
  const activeAgentTicketId = agentJob && ACTIVE_AGENT_STATUSES.includes(agentJob.status)
    ? agentJob.ticket_id
    : null;
  const activePublishJobId = publishJob && ACTIVE_JOB_STATUSES.includes(publishJob.status)
    ? publishJob.id
    : null;
  const activeBacklogJobId = backlogJob && ACTIVE_JOB_STATUSES.includes(backlogJob.status)
    ? backlogJob.id
    : null;

  const loadTickets = useCallback(async () => {
    try {
      const data = await apiGet(`/task-studio/tickets${scope === VIEW_SCOPE_WORKSPACE ? "?scope=workspace" : ""}`, { token });
      setTickets(data.tickets);
      setExpanded(Object.fromEntries(data.tickets.filter((ticket) => ticket.ticket_type === TICKET_TYPE_STORY).map((ticket) => [ticket.id, true])));
      return data;
    } catch (error) {
      setMessage(error.message || "Error loading backlog.");
      return null;
    }
  }, [token, scope]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  useEffect(() => {
    const remembered = readRememberedJob(BACKLOG_GENERATION_JOB_KEY);
    if (!remembered?.id) return;
    setBacklogJob(remembered);
    setLoading(true);
    setMessage("Resuming backlog generation job...");
  }, []);

  useEffect(() => {
    const loadTeam = async () => {
      try {
        const data = await apiGet("/team", { token });
        setTeamMembers(data.members);
      } catch (error) {
        setMessage(error.message || "Error loading team.");
      }
    };
    loadTeam();
  }, [token]);

  const loadAgentJob = useCallback(async (ticketId) => {
    if (!ticketId) return;
    try {
      const data = await apiGet(`/task-studio/tickets/${ticketId}/agent-jobs/latest`, { token });
      setAgentJob(data);
      return data;
    } catch (error) {
      setMessage(error.message || "Error loading the agent job.");
      return null;
    }
  }, [token]);

  useEffect(() => {
    const selectedTicket = tickets.find((ticket) => ticket.id === selectedId);
    const executor = selectedTicket ? membersById[selectedTicket.assignee_id] : null;
    if (executor?.profile_type !== PROFILE_TYPE_AI_AGENT) {
      setAgentJob(null);
      return undefined;
    }
    loadAgentJob(selectedId);
    return undefined;
  }, [loadAgentJob, membersById, selectedId, tickets]);

  useEffect(() => {
    if (!activeAgentTicketId) return undefined;
    return createJobPoller(
      () => loadAgentJob(activeAgentTicketId),
      { shouldContinue: (job) => Boolean(job && ACTIVE_AGENT_STATUSES.includes(job.status)) }
    );
  }, [activeAgentTicketId, loadAgentJob]);

  useEffect(() => {
    if (agentJob?.status === "in_review") loadTickets();
  }, [agentJob?.status, loadTickets]);

  const loadPublishJob = useCallback(async (jobId) => {
    if (!jobId) return null;
    try {
      const data = await apiGet(`/jobs/${jobId}`, { token });
      setPublishJob(data);
      setPublishingCurrent(currentPublishEvent(data)?.metadata?.ticket_title || data.current_step || "Waiting for Jira...");
      setPublishedNames(publishedTicketNames(data));
      if (!ACTIVE_JOB_STATUSES.includes(data.status)) {
        setPublishing(false);
        if (data.status === "completed") {
          const backlog = await loadTickets();
          if (backlog) refreshSelected(backlog.tickets, selectedId, setDraft, setSavedDraft);
          setMessage("Jira publish job completed.");
        } else if (data.status === "failed") {
          setMessage(data.error_message || "Jira publish job failed.");
        }
      }
      return data;
    } catch (error) {
      setPublishing(false);
      setMessage(error.message || "Error loading Jira publish job.");
      return null;
    }
  }, [loadTickets, selectedId, token]);

  useEffect(() => {
    if (!activePublishJobId) return undefined;
    return createJobPoller(
      () => loadPublishJob(activePublishJobId),
      { shouldContinue: (job) => Boolean(job && ACTIVE_JOB_STATUSES.includes(job.status)), immediate: true }
    );
  }, [activePublishJobId, loadPublishJob]);

  const loadBacklogJob = useCallback(async (jobId) => {
    if (!jobId) return null;
    try {
      const data = await apiGet(`/jobs/${jobId}`, { token });
      setBacklogJob(data);
      setMessage(jobStatusMessage(data, "Backlog generation"));
      if (!ACTIVE_JOB_STATUSES.includes(data.status)) {
        forgetRememberedJob(BACKLOG_GENERATION_JOB_KEY);
        setLoading(false);
        if (data.status === "completed") {
          const backlog = data.result_payload;
          setTickets(backlog.tickets);
          setSelectedId(null);
          setDraft(null);
          setSavedDraft(null);
          setExpanded(Object.fromEntries(backlog.tickets.filter((ticket) => ticket.ticket_type === TICKET_TYPE_STORY).map((ticket) => [ticket.id, true])));
          setMessage("Draft backlog generated. Open tickets to review and edit.");
          onUsageChange?.();
        } else if (data.status === "failed") {
          setMessage(data.error_message || "Backlog generation failed.");
        }
      }
      return data;
    } catch (error) {
      forgetRememberedJob(BACKLOG_GENERATION_JOB_KEY);
      setLoading(false);
      setMessage(error.message || "Error loading backlog generation job.");
      return null;
    }
  }, [onUsageChange, token]);

  useEffect(() => {
    if (!activeBacklogJobId) return undefined;
    return createJobPoller(
      () => loadBacklogJob(activeBacklogJobId),
      { shouldContinue: (job) => Boolean(job && ACTIVE_JOB_STATUSES.includes(job.status)), immediate: true }
    );
  }, [activeBacklogJobId, loadBacklogJob]);

  const selectTicket = (ticket) => {
    setSelectedId(ticket.id);
    setDraft(toDraft(ticket));
    setSavedDraft(toDraft(ticket));
    setChatMessages([]);
    setAgentJob(null);
    setClarificationAnswer("");
    setMessage("");
  };

  const startAgent = async () => {
    if (!selected) return;
    setLoading(true);
    setMessage("Starting the AI Engineer...");
    try {
      const data = await apiPost(`/task-studio/tickets/${selected.id}/agent-jobs`, undefined, { token });
      setAgentJob(data);
      setMessage("AI Engineer job queued.");
    } catch (error) {
      setMessage(error.message || "Error starting the AI Engineer.");
    } finally {
      setLoading(false);
    }
  };

  const answerClarification = async () => {
    if (!agentJob || !clarificationAnswer.trim()) return;
    setLoading(true);
    setMessage("Sending clarification...");
    try {
      const data = await apiPost(`/task-studio/agent-jobs/${agentJob.id}/clarifications`, { answer: clarificationAnswer }, { token });
      setAgentJob(data);
      setClarificationAnswer("");
      setMessage("Clarification received. The AI Engineer will resume.");
    } catch (error) {
      setMessage(error.message || "Error sending clarification.");
    } finally {
      setLoading(false);
    }
  };

  const createStory = async () => {
    setLoading(true);
    try {
      const data = await apiPost("/task-studio/tickets", emptyStory, { token });
      setTickets((current) => [...current, data]);
      selectTicket(data);
    } catch (error) {
      setMessage(error.message || "Error creating story.");
    } finally {
      setLoading(false);
    }
  };

  const createSubtask = async (story) => {
    setLoading(true);
    try {
      const data = await apiPost("/task-studio/tickets", {
        ...emptyStory,
        ticket_type: TICKET_TYPE_SUBTASK,
        parent_id: story.id,
        title: `New sub-task for ${story.title}`
      }, { token });
      setTickets((current) => [...current, data]);
      setExpanded((current) => ({ ...current, [story.id]: true }));
      selectTicket(data);
      setMessage("Sub-task created.");
    } catch (error) {
      setMessage(error.message || "Error creating sub-task.");
    } finally {
      setLoading(false);
    }
  };

  const generateBacklog = async () => {
    setLoading(true);
    setMessage("Generating story groups from the latest saved WBS...");
    try {
      const job = await apiPost("/task-studio/generate-from-wbs", undefined, { token, model });
      setBacklogJob(job);
      rememberJob(BACKLOG_GENERATION_JOB_KEY, { id: job.id, status: job.status });
      setMessage("Backlog generation queued.");
    } catch (error) {
      setMessage(error.message || "Error generating backlog.");
      setLoading(false);
    }
  };

  const publishBacklog = async () => {
    setPublishing(true);
    setPublishedNames([]);
    setPublishingCurrent("Preparing Jira publish job...");
    try {
      const job = await apiPost("/task-studio/publish", undefined, { token });
      setPublishJob(job);
      setMessage("Jira backlog publish job queued.");
    } catch (error) {
      setPublishing(false);
      setMessage(error.message || "Error publishing backlog to Jira.");
    }
  };

  const deleteTicket = async () => {
    if (!selected) return;
    const childWarning = selected.ticket_type === TICKET_TYPE_STORY ? " and all of its local Sub-tasks" : "";
    const jiraWarning = selected.jira_issue_key ? " The existing Jira issue will not be deleted." : "";
    if (!window.confirm(`Delete "${selected.title}"${childWarning}?${jiraWarning}`)) return;
    setLoading(true);
    try {
      const data = await apiDelete(`/task-studio/tickets/${selected.id}`, { token });
      setTickets(data.tickets);
      setSelectedId(null);
      setDraft(null);
      setSavedDraft(null);
      setChatMessages([]);
      setMessage("Ticket deleted from Task Studio.");
    } catch (error) {
      setMessage(error.message || "Error deleting ticket.");
    } finally {
      setLoading(false);
    }
  };

  const publishTicket = async () => {
    if (!selected) return;
    setPublishing(true);
    setPublishingCurrent(selected.title);
    setPublishedNames([]);
    setMessage(selected.jira_issue_key ? "Updating ticket in Jira..." : "Publishing ticket to Jira...");
    try {
      const job = await apiPost(`/task-studio/tickets/${selected.id}/publish`, undefined, { token });
      setPublishJob(job);
      setMessage(selected.jira_issue_key ? "Jira ticket update job queued." : "Jira ticket publish job queued.");
    } catch (error) {
      setPublishing(false);
      setMessage(error.message || "Error publishing ticket to Jira.");
    }
  };

  const saveTicket = async () => {
    if (!selected || !draft) return;
    setLoading(true);
    try {
      const data = await apiPut(`/task-studio/tickets/${selected.id}`, draft, { token });
      setTickets((current) => current.map((ticket) => ticket.id === data.id ? data : ticket));
      setDraft(toDraft(data));
      setSavedDraft(toDraft(data));
      setMessage("Ticket saved.");
    } catch (error) {
      setMessage(error.message || "Error saving ticket.");
    } finally {
      setLoading(false);
    }
  };

  const askAssistant = async () => {
    if (!draft || !instruction.trim()) return;
    const request = instruction.trim();
    setInstruction("");
    setLoading(true);
    setChatMessages((current) => [...current, { role: "user", content: request }]);
    try {
      const data = await apiPost("/task-studio/assistant", { ticket: withoutAssignment(draft), instruction: request }, { token, model });
      setDraft((current) => ({ ...current, ...data.revised_ticket }));
      setChatMessages((current) => [...current, { role: "assistant", content: data.message }]);
      onUsageChange?.();
    } catch (error) {
      setChatMessages((current) => [...current, { role: "assistant", content: error.message }]);
    } finally {
      setLoading(false);
    }
  };

  const reorderTicket = async (targetId) => {
    if (!draggedTicketId || draggedTicketId === targetId) return;
    const reordered = moveBacklogTicket(tickets, draggedTicketId, targetId);
    if (!reordered) return;
    setTickets(reordered);
    setDraggedTicketId(null);
    try {
      const data = await apiPut("/task-studio/reorder", {
        tickets: reordered.map(({ id, parent_id, position }) => ({ id, parent_id, position }))
      }, { token });
      setTickets(data.tickets);
      setMessage("Backlog order saved.");
    } catch (error) {
      setMessage(error.message || "Error saving backlog order.");
      loadTickets();
    }
  };

  return (
    <div className="task-studio-page">
      <header className="task-studio-header">
        <div>
          <h1>Task Studio</h1>
          <p>{workspaceView ? "Consolidated backlog from all workstreams." : "Create and refine a Jira-like draft backlog before publishing."}</p>
        </div>
        <div className="task-studio-actions">
          <button onClick={createStory} disabled={loading || publishing || workspaceView}>New ticket</button>
          <button className="primary" onClick={generateBacklog} disabled={loading || publishing || workspaceView}>
            {loading ? "Working..." : "Create backlog from WBS"}
          </button>
          <button className="jira" onClick={publishBacklog} disabled={loading || publishing || workspaceView || !tickets.some((ticket) => !ticket.jira_issue_key) || hasUnsavedChanges}>
            {publishing ? "Sending backlog..." : "Send backlog to Jira"}
          </button>
        </div>
      </header>

      {message && <div className="task-studio-message">{message}</div>}
      {publishing && (
        <div className="task-publish-progress" role="status">
          <strong>Sending now: {publishingCurrent}</strong>
          <span>{publishedNames.length} ticket{publishedNames.length === 1 ? "" : "s"} sent</span>
          {publishJob && <span>{publishJob.progress}% · {publishJob.current_step}</span>}
          {!!publishedNames.length && <div>{publishedNames.join("  •  ")}</div>}
        </div>
      )}

      <div className="task-studio-workspace">
        <section className="task-backlog-panel">
          <div className="task-panel-title">
            <strong>Backlog</strong>
            <span>{tickets.length} work items</span>
          </div>
          {!tickets.length && <p className="task-empty">{workspaceView ? "No workstream backlogs found yet." : "Create a ticket or generate a backlog from the latest saved WBS."}</p>}
          {stories.map((story) => (
            <div key={story.id} className="task-story-group">
              <TicketRow
                ticket={story}
                selected={selectedId === story.id}
                expanded={expanded[story.id]}
                onToggle={() => setExpanded((current) => ({ ...current, [story.id]: !current[story.id] }))}
                onSelect={() => selectTicket(story)}
                onCreateSubtask={() => createSubtask(story)}
                assignee={membersById[story.assignee_id]}
                reviewer={membersById[story.reviewer_id]}
                disabled={loading || publishing}
                draggable={!workspaceView}
                dragging={draggedTicketId === story.id}
                onDragStart={() => setDraggedTicketId(story.id)}
                onDragEnd={() => setDraggedTicketId(null)}
                onDrop={() => reorderTicket(story.id)}
              />
              {expanded[story.id] && tickets
                .filter((ticket) => ticket.parent_id === story.id)
                .map((ticket) => (
                  <TicketRow
                    key={ticket.id}
                    ticket={ticket}
                    child
                    selected={selectedId === ticket.id}
                    onSelect={() => selectTicket(ticket)}
                    assignee={membersById[ticket.assignee_id]}
                    reviewer={membersById[ticket.reviewer_id]}
                    disabled={loading || publishing}
                    draggable={!workspaceView}
                    dragging={draggedTicketId === ticket.id}
                    onDragStart={() => setDraggedTicketId(ticket.id)}
                    onDragEnd={() => setDraggedTicketId(null)}
                    onDrop={() => reorderTicket(ticket.id)}
                  />
                ))}
            </div>
          ))}
        </section>

        <section className="task-editor-panel">
          {!draft ? (
            <div className="task-empty editor">Select a ticket to view{workspaceView ? " its details." : " and edit its details."}</div>
          ) : (
            <>
              <div className="task-editor-scroll">
                <div className="task-editor-heading">
                  <span>{draft.ticket_type}</span>
                  <strong>
                    {selected?.jira_issue_key
                      ? <a href={selected.jira_issue_url} target="_blank" rel="noreferrer">{selected.jira_issue_key}</a>
                      : hasUnsavedChanges ? "Unsaved changes" : "Saved"}
                  </strong>
                </div>
                <label>Title<input value={draft.title} disabled={workspaceView} onChange={(event) => updateDraft(setDraft, "title", event.target.value)} /></label>
                <div className="task-field-grid">
                  <label>Type<select value={draft.ticket_type} disabled={workspaceView} onChange={(event) => updateDraft(setDraft, "ticket_type", event.target.value)}><option>{TICKET_TYPE_STORY}</option><option>{TICKET_TYPE_SUBTASK}</option></select></label>
                  <label>Complexity<select value={draft.complexity} disabled={workspaceView} onChange={(event) => updateDraft(setDraft, "complexity", Number(event.target.value))}>{FIBONACCI.map((value) => <option key={value}>{value}</option>)}</select></label>
                  <label>Jira sync status<input value={draft.status} disabled /></label>
                  <label>Delivery status<select value={draft.delivery_status} disabled={workspaceView} onChange={(event) => updateDraft(setDraft, "delivery_status", event.target.value)}>{DELIVERY_STATUSES.map((status) => <option key={status}>{status}</option>)}</select></label>
                  <label>Planned start<input value={formatDate(selected?.planned_start_date)} disabled /></label>
                  <label>Due date<input value={formatDate(selected?.due_date)} disabled /></label>
                </div>
                <label>
                  Executor
                  <select
                    value={draft.assignee_id || ""}
                    disabled={workspaceView}
                    onChange={(event) => setDraft((current) => ({
                      ...current,
                      assignee_id: Number(event.target.value) || null,
                      reviewer_id: null
                    }))}
                  >
                    <option value="">Unassigned</option>
                    {!!humanMembers.length && <optgroup label="Human engineers">{humanMembers.map((member) => <option key={member.id} value={member.id}>{member.name}{member.job_title ? ` - ${member.job_title}` : ""}</option>)}</optgroup>}
                    {!!aiMembers.length && <optgroup label="AI Engineers">{aiMembers.map((member) => <option key={member.id} value={member.id}>{member.name} · {agentModelLabel(member.agent_model)}</option>)}</optgroup>}
                  </select>
                </label>
                {requiresReviewer && (
                  <label htmlFor="accountable-reviewer">
                    Accountable human reviewer
                    <select id="accountable-reviewer" aria-label="Accountable human reviewer" value={draft.reviewer_id || ""} disabled={workspaceView} onChange={(event) => updateDraft(setDraft, "reviewer_id", Number(event.target.value) || null)}>
                      <option value="">Select a human reviewer</option>
                      {humanMembers.map((member) => <option key={member.id} value={member.id}>{member.name}{member.job_title ? ` - ${member.job_title}` : ""}</option>)}
                    </select>
                    <small className="task-field-help">The reviewer owns clarification, code review, validation, corrections, and final approval.</small>
                  </label>
                )}
                {draft.ticket_type === TICKET_TYPE_SUBTASK && (
                  <label>
                    Parent Story
                    <select
                      value={draft.parent_id || ""}
                      disabled={workspaceView}
                      onChange={(event) => updateDraft(setDraft, "parent_id", Number(event.target.value) || null)}
                    >
                      <option value="">Select a parent Story</option>
                      {stories
                        .filter((story) => story.id !== selectedId)
                        .map((story) => <option key={story.id} value={story.id}>{story.title}</option>)}
                    </select>
                  </label>
                )}
                <label>Description<textarea rows="8" value={draft.description} disabled={workspaceView} onChange={(event) => updateDraft(setDraft, "description", event.target.value)} /></label>
                <ListField label="Acceptance criteria" value={draft.acceptance_criteria} disabled={workspaceView} onChange={(value) => updateDraft(setDraft, "acceptance_criteria", value)} />
                <ListField label="Dependencies" value={draft.dependencies} disabled={workspaceView} onChange={(value) => updateDraft(setDraft, "dependencies", value)} />
                <ListField label="Assumptions" value={draft.assumptions} disabled={workspaceView} onChange={(value) => updateDraft(setDraft, "assumptions", value)} />
                {requiresReviewer && (
                  <AgentJobPanel
                    job={agentJob}
                    answer={clarificationAnswer}
                    onAnswerChange={setClarificationAnswer}
                    onAnswer={answerClarification}
                    loading={loading}
                  />
                )}
                <div className="task-ticket-actions">
                  <button className="task-save-button" onClick={saveTicket} disabled={loading || workspaceView || !hasUnsavedChanges || (requiresReviewer && !draft.reviewer_id)}>Save ticket</button>
                  {requiresReviewer && (
                    <button
                      className="task-agent-button"
                      onClick={startAgent}
                      disabled={loading || workspaceView || hasUnsavedChanges || !draft.reviewer_id || (agentJob && [...ACTIVE_AGENT_STATUSES, AGENT_STATUS_WAITING_FOR_CLARIFICATION].includes(agentJob.status))}
                    >
                      {agentJob ? "Start new Agent job" : "Start Agent"}
                    </button>
                  )}
                  <button
                    className="task-jira-button"
                    onClick={publishTicket}
                    disabled={loading || publishing || workspaceView || hasUnsavedChanges}
                  >
                    {selected?.jira_issue_key ? "Update Jira ticket" : "Send ticket to Jira"}
                  </button>
                  <button className="task-delete-button" onClick={deleteTicket} disabled={loading || publishing || workspaceView}>
                    Delete ticket
                  </button>
                </div>
              </div>
              <aside className="task-assistant">
                <h3>Ticket assistant</h3>
                <div className="task-assistant-chat">
                  {!chatMessages.length && <p>Ask the assistant to rewrite, estimate, split, clarify, or update ticket fields.</p>}
                  {chatMessages.map((item, index) => <div key={index} className={`task-chat-message ${item.role}`}>{item.content}</div>)}
                </div>
                <textarea value={instruction} onChange={(event) => setInstruction(event.target.value)} placeholder="How should this ticket change?" />
                <button onClick={askAssistant} disabled={loading || workspaceView || !instruction.trim()}>Send to assistant</button>
              </aside>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default TaskStudioPage;
